import React, { useEffect } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { Form, Input, DatePicker, Select } from "antd";
import CrudButtons from "components/button/crudbuttons";
import FormItem from "components/fields/FormItem";
import dayjs from "dayjs";
import { requiredField } from "utils/crudSagaHelper";
import {
  getEmployeeSalaryDetails,
  doEmployeeSalaryCreate,
  doEmployeeSalaryUpdate,
} from "../../../../../redux/salary/salaryState";
import { DATE_FORMAT } from "variables/constants";
import { getLabel } from "utils";
import { MODULE_SALARIES } from "variables/constants";

const EmployeeSalaryForm = ({ 
  onCancel, 
  employeeId,
  submitLabel,
  ...props
}) => {
  const [form] = Form.useForm();
  const { recordId, mode } = props;
  const { TextArea } = Input;
  const dispatch = useDispatch();

  const recordData = useSelector((state) =>  state.salary.employeeSalaryDetails?.data);

  const doCreate = (data) => dispatch(doEmployeeSalaryCreate(data));
  const doUpdate = (data) => dispatch(doEmployeeSalaryUpdate(data));

  useEffect(() => {
    if (recordData) {
      form.resetFields();
      const { salary:sal_employee_id, sal_amount, sal_effective_date, sal_end_date, ...rest } = recordData;

      const initialValues = {
        ...rest,
        sal_employee_id: employeeId,
        sal_amount: recordData?.sal_amount ?? "",
        sal_effective_date: recordData?.sal_effective_date ? dayjs(recordData.sal_effective_date) : null,
        sal_end_date: recordData?.sal_end_date ? dayjs(recordData.sal_end_date) : null,
      };

      form.setFieldsValue(initialValues);
    }
  }, [recordData, employeeId, form]);

    useEffect(() => {
      dispatch(getEmployeeSalaryDetails({ recordId, employeeId }));
  }, [employeeId, recordId, dispatch]);

  const onSubmit = (formData) => { 
    try {
      formData.sal_effective_date = formData.sal_effective_date?.format(DATE_FORMAT);
      formData.sal_end_date = formData.sal_end_date?.format(DATE_FORMAT);
      formData.sal_amount = parseFloat(formData.sal_amount);

      recordData?.sal_id > 0 ? doUpdate(formData) : doCreate(formData);
      
    } catch (error) {
      console.error("Save failed:", error);
    }
  };

  return (
    <Form className={mode !== "view" ? "form" : ""}
      layout="vertical" 
      form={form} 
      onFinish={onSubmit}
    > 
      <FormItem
				name="sal_employee_id"
				hidden
			>
				<Input type="hidden"/>
			</FormItem>
      <FormItem
        name="sal_id"
        hidden
      >
        <Input type="hidden"/>
      </FormItem>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-y-2 gap-x-4 px-2 w-full">
        <FormItem className="font-medium mb-2 col-span-3 md:col-span-1"
          name="sal_type_id"
          label="Type"
          rules={[requiredField()]}
        >
          {mode !== "view" ?
            <Select id="sal_type_id"
              className="input-field"
              showSearch
              allowClear={true}
              options={recordData?.salaryTypeList}
            /> 
            : 
            <label className="text-base label-value dark:text-white">
              {getLabel(recordData?.salaryTypeList, recordData?.sal_type_id)}
            </label>
          }              
        </FormItem>
        <FormItem className="font-medium mb-2 col-span-3 md:col-span-1"
          name="sal_amount"
          label="Amount"
          rules={[ requiredField(),
            {
              pattern: /^\d{1,8}(\.\d{1,2})?$/,
              message: "Enter a valid amount (max 99999999.99, up to 2 decimal places)",
            }
          ]}
        >
          {mode !== "view" ? 
            <Input type="text" 
              id="sal_amount"
              className="input-field"
              maxLength={12}
              placeholder="0.00"
            />
            : 
            <label className="text-base label-value dark:text-white">
              {recordData?.sal_amount}
            </label>
          }
        </FormItem>	
        <FormItem className="font-medium mb-2 col-span-3 md:col-span-1"
          name="sal_currency_id"
          label="Currency"
          rules={[requiredField()]}
        >
          {mode !== "view" ?
            <Select id="sal_currency_id"
              className="input-field"
              showSearch
              allowClear={true}
              options={recordData?.currencyList}
            />
            : 
            <label className="text-base label-value dark:text-white">
              {getLabel(recordData?.currencyList, recordData?.sal_currency_id)}
            </label>
          }
        </FormItem>
        <FormItem className="font-medium mb-2 col-span-3 md:col-span-1"
            label="Effective Date"
            name="sal_effective_date"
            rules={[requiredField()]}
        >
          {mode !== "view" ?
            <DatePicker id="sal_effective_date"
              className="input-field"
              format={DATE_FORMAT}
              style={{ width: "100%" }}
            />
           : 
            <label className="text-base label-value dark:text-white">
              {recordData?.sal_effective_date}
            </label>
          }
        </FormItem>
        <FormItem className="font-medium mb-2 col-span-3 md:col-span-1"
          label="End Date" 
          name="sal_end_date"
        >
          {mode !== "view" ?
            <DatePicker id="sal_end_date"
              className="input-field"
              format={DATE_FORMAT}
              style={{ width: "100%" }}
            />
           : 
            <label className="text-base label-value dark:text-white">
              {recordData?.sal_end_date}
            </label>
          }
        </FormItem>
        <FormItem className="font-medium  mb-2 col-span-3"
          name="sal_notes"
          label="Notes"
        >
          {mode !== "view" ?
            <TextArea id="sal_notes"
              className="input-field"
              rows={2}
              maxLength={300}		            		
            />
            : 
            <label className="text-base label-value dark:text-white">
              {recordData?.sal_notes}
            </label>
          }
        </FormItem>
      </div>

      <div className="w-full flex justify-end">
        {(mode === "edit") && (
          <CrudButtons
            mode={mode}
            recordId={recordData?.sal_id ?? 0}
            isModal={true}
            modalId={recordData?.sal_id > 0 ? 'edit_salary' : 'add_salary'}
            cancelLabel="Cancel"
            doHideModal={onCancel}
            submitLabel={submitLabel}
            moduleId={MODULE_SALARIES}
          />
        )}
      </div>
    </Form>
  );
};

export default (EmployeeSalaryForm);
